import {
  Button,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@/components/ui";
import { phaseTeamMembersOptions } from "@/lib/data/react-query/matters/matter-phase";
import { useQuery } from "@tanstack/react-query";
import { Portal } from "@radix-ui/react-hover-card";
import { Users } from "lucide-react";
import { useState, type ReactElement } from "react";

export function PhaseTeamRoster({
  teamId,
  hoverTrigger,
  clickOpens = true,
}: {
  teamId: string;
  hoverTrigger?: ReactElement;
  clickOpens?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    ...phaseTeamMembersOptions(teamId),
    enabled: open,
  });
  const members = (
    <div className="space-y-3">
      <Text variant="body">Team members</Text>
      {query.isLoading ? (
        <Text variant="caption">Loading members…</Text>
      ) : query.isError ? (
        <Text variant="caption">Unable to load team members.</Text>
      ) : query.data?.data.length ? (
        query.data.data.map((member) => (
          <div key={member.userId}>
            <Text variant="caption">{member.label}</Text>
            <Text variant="caption" className="text-muted-foreground">
              {member.roleLabel}
            </Text>
          </div>
        ))
      ) : (
        <Text variant="caption">This team has no members.</Text>
      )}
    </div>
  );

  if (hoverTrigger) {
    return (
      <HoverCard open={open} onOpenChange={setOpen} openDelay={150}>
        <HoverCardTrigger
          asChild
          onClick={
            clickOpens
              ? (event) => {
                  event.stopPropagation();
                  setOpen(true);
                }
              : undefined
          }
        >
          {hoverTrigger}
        </HoverCardTrigger>
        <Portal>
          <HoverCardContent
            align="start"
            className="z-[110] max-h-80 overflow-y-auto"
            data-modal-popover="true"
          >
            {members}
          </HoverCardContent>
        </Portal>
      </HoverCard>
    );
  }

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="gap-2">
          <Users className="size-4" />
          View members
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" data-modal-popover="true">
        {members}
      </PopoverContent>
    </Popover>
  );
}
